import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Card,
    Row,
    Col,
    Typography,
    Tag,
    Badge,
    Button,
    Select,
    Tabs,
    Progress,
    Statistic,
    Table,
    List,
    Tooltip,
    Divider,
    Avatar,
    Alert,
    Skeleton,
    Space,
    message
} from 'antd';
import {
    UserOutlined,
    BankOutlined,
    SafetyCertificateOutlined,
    CalendarOutlined,
    TeamOutlined,
    CloudServerOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    AlertOutlined,
    EditOutlined,
    UnorderedListOutlined,
    GlobalOutlined,
    MailOutlined,
    PhoneOutlined,
    LinkOutlined,
    DashboardOutlined,
    SettingOutlined,
    PieChartOutlined,
    CheckSquareOutlined,
    CustomerServiceOutlined,
    HistoryOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    ApartmentOutlined,
    AppstoreOutlined,
    ThunderboltOutlined,
    CrownOutlined,
    AuditOutlined,
    IdcardOutlined,
    DollarOutlined,
    FireOutlined
} from '@ant-design/icons';
import './Client360View.css';

const { Title, Text, Paragraph } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

// Reusable clean Key-Value item that avoids table squishing
const DetailField = ({ label, value, icon, copyable, isLink, linkHref, extraTag }) => (
    <div style={{
        background: '#f8fafc',
        padding: '12px 16px',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    }}>
        <div style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: '#64748b',
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 6
        }}>
            {icon}
            <span>{label}</span>
        </div>
        <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#0f172a',
            wordBreak: 'break-word',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap'
        }}>
            {isLink && linkHref ? (
                <a href={linkHref} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>
                    {value || '—'} <LinkOutlined style={{ fontSize: 11 }} />
                </a>
            ) : copyable ? (
                <Text copyable style={{ color: '#0f172a', fontWeight: 600 }}>{value || '—'}</Text>
            ) : (
                <span>{value || '—'}</span>
            )}
            {extraTag}
        </div>
    </div>
);

const Client360View = () => {
    const { adminId } = useParams();
    const navigate = useNavigate();

    const [authChecked, setAuthChecked] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [clientsList, setClientsList] = useState([]);
    const [selectedAdminId, setSelectedAdminId] = useState(adminId || '');
    const [clientData, setClientData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    // 1. Verify Superadmin permission
    useEffect(() => {
        try {
            const role = (localStorage.getItem('role') || '').toUpperCase();
            if (role === 'SUPERADMIN' || role === 'INTERNALSTAFF') {
                setIsAuthorized(true);
            } else {
                message.error('You do not have permission to view the Client 360° Hub.');
                navigate('/dashboard', { replace: true });
            }
        } catch (error) {
            message.error('Unable to verify permissions.');
            navigate('/dashboard', { replace: true });
        } finally {
            setAuthChecked(true);
        }
    }, [navigate]);

    // 2. Fetch the clients selector list
    useEffect(() => {
        if (!isAuthorized) return;

        const fetchClientsList = async () => {
            try {
                setListLoading(true);
                const res = await axios.get(`${API_URL}api/superadmin/client-360/list/`, { withCredentials: true });
                if (res.data?.status === 'SUCCESS' && Array.isArray(res.data?.clients)) {
                    setClientsList(res.data.clients);

                    // If no adminId in URL, default to the first client
                    if (!adminId && res.data.clients.length > 0) {
                        const firstId = res.data.clients[0].admin_id;
                        setSelectedAdminId(firstId);
                        navigate(`/client_360/${firstId}`, { replace: true });
                    }
                }
            } catch (err) {
                console.error('Error fetching client list:', err);
                message.error('Failed to load client selector list.');
            } finally {
                setListLoading(false);
            }
        };

        fetchClientsList();
    }, [isAuthorized, adminId, navigate]);

    // Keep selectedAdminId aligned with URL params
    useEffect(() => {
        if (adminId) {
            setSelectedAdminId(adminId);
        }
    }, [adminId]);

    // 3. Fetch full 360 data for the active client
    useEffect(() => {
        if (!isAuthorized || !selectedAdminId) return;

        const fetchClient360 = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`${API_URL}api/superadmin/client-360/${selectedAdminId}/`, { withCredentials: true });
                if (res.data?.status === 'SUCCESS' && res.data?.client_360) {
                    setClientData(res.data.client_360);
                } else {
                    throw new Error(res.data?.message || 'Client data not found');
                }
            } catch (err) {
                const msg = err.response?.data?.message || 'Failed to load Client 360° profile.';
                console.error(msg, err);
                message.error(msg);
                setClientData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchClient360();
    }, [isAuthorized, selectedAdminId]);

    // Client change handler from the Select dropdown
    const handleClientChange = (value) => {
        if (value) {
            setSelectedAdminId(value);
            navigate(`/client_360/${value}`);
        }
    };

    const cp = clientData?.client_profile || {};
    const pkg = clientData?.package_and_licensing || {};
    const gov = clientData?.governance_team || {};
    const scope = clientData?.scope || {};
    const telemetry = clientData?.telemetry_and_usage || {};
    const risk = clientData?.risk_and_support || {};
    const health = clientData?.client_health || {};
    const audit = clientData?.audit || {};
    const usersDir = clientData?.users_directory || {};
    const internalUsers = usersDir.internal_users || usersDir.provisioned_users || [];
    const tprmAdmins = usersDir.tprm_admins || [];
    const tprmUsers = usersDir.tprm_users || [];
    const primaryAdmin = usersDir.primary_admin || {};
    const userCounts = usersDir.counts || {
        internal: internalUsers.length,
        tprm_admins: tprmAdmins.length,
        tprm_users: tprmUsers.length,
        total: internalUsers.length + tprmAdmins.length + tprmUsers.length + 1
    };

    const healthColor = health.indicator === 'green' ? '#10b981' : (health.indicator === 'amber' ? '#f59e0b' : '#ef4444');
    const healthTagColor = health.indicator === 'green' ? 'success' : (health.indicator === 'amber' ? 'warning' : 'error');

    // Table columns for Support Tickets
    const ticketColumns = [
        {
            title: 'Request ID',
            dataIndex: 'request_id',
            key: 'request_id',
            render: (text) => <Text code>{text || '—'}</Text>
        },
        {
            title: 'Description',
            dataIndex: 'details',
            key: 'details',
            ellipsis: true,
            render: (text) => <Text>{text || '—'}</Text>
        },
        {
            title: 'Date Logged',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => <Text type="secondary">{text || '—'}</Text>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'open' ? 'gold' : (status === 'resolved' ? 'green' : 'blue')}>
                    {(status || 'Open').toUpperCase()}
                </Tag>
            )
        }
    ];

    const tprmAdminColumns = [
        {
            title: 'Vendor & Admin',
            key: 'admin_info',
            render: (_, record) => (
                <Space>
                    <Avatar style={{ backgroundColor: '#f97316', fontWeight: 600 }}>
                        {record.company_name ? record.company_name[0].toUpperCase() : 'V'}
                    </Avatar>
                    <div>
                        <Text strong style={{ display: 'block', fontSize: 13 }}>{record.name}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{record.username}</Text>
                    </div>
                </Space>
            )
        },
        {
            title: 'TPIN',
            dataIndex: 'tpin',
            key: 'tpin',
            width: 100,
            render: (tpin) => <Tag color="orange" style={{ fontWeight: 600 }}>{tpin || '—'}</Tag>
        },
        {
            title: 'Vendor Company',
            key: 'company',
            render: (_, record) => (
                <div>
                    <Text strong style={{ fontSize: 13, display: 'block' }}>{record.company_name || '—'}</Text>
                    {record.company_website && (
                        <a href={record.company_website.startsWith('http') ? record.company_website : `https://${record.company_website}`} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                            <LinkOutlined style={{ marginRight: 4 }} />
                            {record.company_website.replace(/^https?:\/\//, '')}
                        </a>
                    )}
                </div>
            )
        },
        {
            title: 'Contact Details',
            key: 'contact',
            render: (_, record) => (
                <div>
                    <div>
                        <MailOutlined style={{ marginRight: 5, color: '#3b82f6', fontSize: 12 }} />
                        <Text copyable style={{ fontSize: 12 }}>{record.email || '—'}</Text>
                    </div>
                    {record.phone && (
                        <div style={{ marginTop: 2 }}>
                            <PhoneOutlined style={{ marginRight: 5, color: '#10b981', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>{record.phone}</Text>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Classification',
            key: 'classification',
            render: (_, record) => (
                <div>
                    <Tag color="cyan" style={{ fontSize: 11, marginBottom: 2 }}>{record.category || 'General'}</Tag>
                    <div>
                        <Tag color="purple" style={{ fontSize: 10 }}>{record.vendor_type || 'Third-Party'}</Tag>
                    </div>
                </div>
            )
        },
        {
            title: 'Risk Posture',
            key: 'risk',
            render: (_, record) => (
                <div>
                    <Tag color={record.risk_rating === 'High' ? 'red' : (record.risk_rating === 'Moderate' ? 'gold' : 'green')} style={{ fontWeight: 600 }}>
                        {record.risk_rating || 'Unrated'}
                    </Tag>
                    {Number(record.risk_score || 0) > 0 && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Score: {record.risk_score}</Text>
                    )}
                </div>
            )
        },
        {
            title: 'Review Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Badge
                    status={status === 'Active' ? 'success' : (status === 'Under Review' ? 'processing' : 'default')}
                    text={<Text strong style={{ fontSize: 12 }}>{status}</Text>}
                />
            )
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => <Text type="secondary" style={{ fontSize: 12 }}>{date ? date.slice(0, 10) : '—'}</Text>
        }
    ];

    const tprmUserColumns = [
        {
            title: 'TPRM User',
            key: 'user_info',
            render: (_, record) => (
                <Space>
                    <Avatar style={{ backgroundColor: '#8b5cf6', fontWeight: 600 }}>
                        {record.name ? record.name[0].toUpperCase() : 'U'}
                    </Avatar>
                    <div>
                        <Text strong style={{ display: 'block', fontSize: 13 }}>{record.name}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{record.username}</Text>
                    </div>
                </Space>
            )
        },
        {
            title: 'UID',
            dataIndex: 'uid',
            key: 'uid',
            width: 130,
            render: (uid) => <Text code>{uid || '—'}</Text>
        },
        {
            title: 'Linked Vendor & TPIN',
            key: 'vendor',
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Text strong style={{ fontSize: 12 }}>{record.company_name || '—'}</Text>
                    <Tag color="orange" style={{ fontSize: 11 }}>{record.tpin || '—'}</Tag>
                </Space>
            )
        },
        {
            title: 'Contact Details',
            key: 'contact',
            render: (_, record) => (
                <div>
                    <div>
                        <MailOutlined style={{ marginRight: 5, color: '#3b82f6', fontSize: 12 }} />
                        <Text copyable style={{ fontSize: 12 }}>{record.email || '—'}</Text>
                    </div>
                    {record.phone && (
                        <div style={{ marginTop: 2 }}>
                            <PhoneOutlined style={{ marginRight: 5, color: '#10b981', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>{record.phone}</Text>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Security Setup',
            key: 'security',
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Tag color={record.is_password_set ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                        {record.is_password_set ? 'Password Active' : 'Password Pending'}
                    </Tag>
                    {record.agreement_accepted ? (
                        <Tag color="cyan" style={{ fontSize: 10 }}>MSA Accepted</Tag>
                    ) : null}
                </Space>
            )
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => <Text type="secondary" style={{ fontSize: 12 }}>{date ? date.slice(0, 10) : '—'}</Text>
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Badge
                    status={status === 'Active' ? 'success' : 'error'}
                    text={<Text strong style={{ fontSize: 12 }}>{status}</Text>}
                />
            )
        }
    ];

    // Table columns for Administrative Actions
    const moduleUsage20d = telemetry.module_usage_20d || scope.module_usage_20d || {};

    const moduleUsageColumns = [
        {
            title: 'Platform Module',
            dataIndex: 'module',
            key: 'module',
            render: (modName, record) => (
                <Space>
                    <ThunderboltOutlined style={{ color: record.activity_count > 0 ? '#10b981' : '#94a3b8' }} />
                    <Text strong={record.activity_count > 0}>{modName}</Text>
                </Space>
            )
        },
        {
            title: 'Activities (Last 20 Days)',
            dataIndex: 'activity_count',
            key: 'activity_count',
            sorter: (a, b) => a.activity_count - b.activity_count,
            defaultSortOrder: 'descend',
            render: (count) => (
                <Badge 
                    count={count} 
                    showZero 
                    style={{ 
                        backgroundColor: count >= 10 ? '#10b981' : (count >= 3 ? '#faad14' : (count > 0 ? '#1890ff' : '#e2e8f0')),
                        color: count === 0 ? '#64748b' : '#fff',
                        fontWeight: 600,
                        fontSize: 12
                    }} 
                />
            )
        },
        {
            title: 'Activity Share',
            dataIndex: 'percentage',
            key: 'percentage',
            width: 170,
            render: (pct, record) => (
                <Progress 
                    percent={pct} 
                    size="small" 
                    strokeColor={record.activity_count >= 10 ? '#10b981' : (record.activity_count >= 3 ? '#faad14' : '#1890ff')}
                    format={(p) => `${p}%`}
                />
            )
        },
        {
            title: 'Engagement Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <Tag color={record.status_color || 'default'} style={{ borderRadius: 6 }}>
                    {status}
                </Tag>
            )
        },
        {
            title: 'Last Activity Recorded',
            dataIndex: 'last_activity_date',
            key: 'last_activity_date',
            render: (date, record) => date ? (
                <Tooltip title={record.latest_action || 'Action logged'}>
                    <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {date}
                    </Text>
                </Tooltip>
            ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
            )
        }
    ];

    const provisionedUserColumns = [
        {
            title: 'Team Member',
            key: 'user_info',
            render: (_, record) => (
                <Space>
                    <Avatar style={{ backgroundColor: record.status === 'Active' ? '#3b82f6' : '#94a3b8', fontWeight: 600 }}>
                        {record.name ? record.name[0].toUpperCase() : 'U'}
                    </Avatar>
                    <div>
                        <Text strong style={{ display: 'block', fontSize: 13 }}>{record.name}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{record.username}</Text>
                    </div>
                </Space>
            )
        },
        {
            title: 'UID',
            dataIndex: 'uid',
            key: 'uid',
            width: 130,
            render: (text) => <Text code>{text || '—'}</Text>
        },
        {
            title: 'Contact Details',
            key: 'contact',
            render: (_, record) => (
                <div>
                    <div>
                        <MailOutlined style={{ marginRight: 5, color: '#3b82f6', fontSize: 12 }} />
                        <Text copyable style={{ fontSize: 12 }}>{record.email || '—'}</Text>
                    </div>
                    {record.phone && (
                        <div style={{ marginTop: 2 }}>
                            <PhoneOutlined style={{ marginRight: 5, color: '#10b981', fontSize: 12 }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>{record.phone}</Text>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Role',
            dataIndex: 'role_name',
            key: 'role_name',
            render: (role) => (
                <Tag color="geekblue" style={{ borderRadius: 6, fontWeight: 500 }}>
                    {role || 'User'}
                </Tag>
            )
        },
        {
            title: 'Org Placement',
            key: 'placement',
            render: (_, record) => (
                <div>
                    <Text style={{ fontSize: 12, display: 'block' }}>
                        <ApartmentOutlined style={{ marginRight: 4, color: '#8b5cf6' }} />
                        <strong>Dept:</strong> {record.department || '—'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        <GlobalOutlined style={{ marginRight: 4, color: '#06b6d4' }} />
                        <strong>BU:</strong> {record.business_unit || '—'}
                    </Text>
                </div>
            )
        },
        {
            title: 'Access & Agreements',
            key: 'security',
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Tag color={record.is_password_set ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                        {record.is_password_set ? 'Password Active' : 'Password Pending'}
                    </Tag>
                    {record.agreement_accepted ? (
                        <Tag color="cyan" style={{ fontSize: 10 }}>MSA Accepted</Tag>
                    ) : (
                        <Tag color="default" style={{ fontSize: 10 }}>MSA Pending</Tag>
                    )}
                </Space>
            )
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {text ? text.slice(0, 10) : '—'}
                </Text>
            )
        },
        {
            title: 'Last Login',
            dataIndex: 'last_login',
            key: 'last_login',
            render: (text) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {text}
                </Text>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Badge
                    status={status === 'Active' ? 'success' : 'error'}
                    text={<Text strong style={{ fontSize: 12, color: status === 'Active' ? '#10b981' : '#ef4444' }}>{status}</Text>}
                />
            )
        }
    ];

    const auditColumns = [
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 190,
            render: (ts) => <Text type="secondary" style={{ fontFamily: 'monospace' }}>{ts || '—'}</Text>
        },
        {
            title: 'Action Performed',
            dataIndex: 'action',
            key: 'action',
            render: (act) => <Tag color="blue">{act || 'System Action'}</Tag>
        },
        {
            title: 'User / Admin',
            dataIndex: 'user',
            key: 'user',
            render: (u) => <Text strong>{u || 'System'}</Text>
        },
        {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
            ellipsis: true,
            render: (d) => <Text type="secondary">{d || '—'}</Text>
        }
    ];

    // Client Selector options
    const selectOptions = useMemo(() => {
        return clientsList.map(c => ({
            label: `${c.company_name} (${c.package})`,
            value: c.admin_id
        }));
    }, [clientsList]);

    if (!authChecked) {
        return null;
    }

    return (
        <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh', marginTop: '50px' }}>
            <div style={{ maxWidth: 1600, margin: '0 auto' }}>

                {/* 1. Header Banner & Quick Switcher Card */}
                <Card
                    style={{
                        marginBottom: 24,
                        borderRadius: 16,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e2e8f0',
                        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                        color: '#fff'
                    }}
                    bodyStyle={{ padding: '24px 32px' }}
                >
                    {loading ? (
                        <Row align="middle" justify="space-between" gutter={[24, 24]}>
                            <Col xs={24} md={16}>
                                <Space size="middle" align="center">
                                    <Skeleton.Avatar active size={64} shape="square" />
                                    <div>
                                        <Skeleton active title={{ width: 250 }} paragraph={{ rows: 1, width: 350 }} />
                                    </div>
                                </Space>
                            </Col>
                            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                                <Skeleton.Input active size="large" style={{ width: 240 }} />
                            </Col>
                        </Row>
                    ) : (
                        <Row align="middle" justify="space-between" gutter={[24, 24]}>
                            <Col xs={24} lg={15}>
                                <Space align="center" size={20}>
                                    <Avatar
                                        shape="square"
                                        size={68}
                                        src={cp.logo_url}
                                        icon={<BankOutlined />}
                                        style={{
                                            backgroundColor: '#3b82f6',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                            fontSize: 28,
                                            fontWeight: 700
                                        }}
                                    >
                                        {(cp.company_name || 'C').charAt(0).toUpperCase()}
                                    </Avatar>
                                    <div>
                                        <Space align="center" size="small" wrap>
                                            <Title level={3} style={{ color: '#fff', margin: 0 }}>
                                                {cp.company_name || 'Client 360° Profile'}
                                            </Title>
                                            {cp.uid && (
                                                <Tag color="rgba(255,255,255,0.2)" style={{ color: '#fff', border: 'none' }}>
                                                    UID: {cp.uid}
                                                </Tag>
                                            )}
                                            {cp.is_deactivated ? (
                                                <Tag color="error">Deactivated</Tag>
                                            ) : (
                                                <Tag color="success">Active Tenant</Tag>
                                            )}
                                        </Space>
                                        <Paragraph style={{ color: '#94a3b8', margin: '4px 0 0 0' }}>
                                            <Space split="•" size="small" wrap>
                                                {[
                                                    <span key="legal"><BankOutlined /> {cp.legal_entity_name || cp.company_name}</span>,
                                                    cp.industry ? <span key="ind"><ApartmentOutlined /> {cp.industry}</span> : null,
                                                    cp.country ? <span key="cntry"><GlobalOutlined /> {cp.country}</span> : null,
                                                    <span key="pkg"><CrownOutlined /> {pkg.package_name || 'ENTERPRISE'}</span>
                                                ].filter(Boolean)}
                                            </Space>
                                        </Paragraph>
                                    </div>
                                </Space>
                            </Col>

                            <Col xs={24} lg={9}>
                                <Space direction="vertical" align="end" size="small" style={{ width: '100%' }}>
                                    <Space size="middle" wrap>
                                        <Text style={{ color: '#cbd5e1', fontWeight: 600 }}>Switch Client:</Text>
                                        <Select
                                            size="large"
                                            showSearch
                                            placeholder="Select client"
                                            optionFilterProp="label"
                                            value={selectedAdminId}
                                            onChange={handleClientChange}
                                            options={selectOptions}
                                            loading={listLoading}
                                            style={{ minWidth: 260 }}
                                            dropdownStyle={{ zIndex: 1050 }}
                                        />
                                    </Space>

                                    <Space size="small" wrap style={{ marginTop: 8 }}>
                                        <Button
                                            type="primary"
                                            icon={<EditOutlined />}
                                            onClick={() => navigate(`/edit_cara_admin/${cp.admin_id}`)}
                                        >
                                            Edit Client Profile
                                        </Button>
                                        <Button
                                            ghost
                                            icon={<UnorderedListOutlined />}
                                            onClick={() => navigate('/superadmin_manage_admins')}
                                            style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
                                        >
                                            Manage Admins
                                        </Button>
                                    </Space>
                                </Space>
                            </Col>
                        </Row>
                    )}
                </Card>

                {/* 2. Executive KPI & Health Score Bar */}
                <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
                    {/* Card 1: Client Health Score */}
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
                                height: '100%',
                                borderTop: `4px solid ${loading ? '#d9d9d9' : healthColor}`
                            }}
                        >
                            {loading ? (
                                <Skeleton active paragraph={{ rows: 3 }} />
                            ) : (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <Text type="secondary" strong style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                                            Client Health Score
                                        </Text>
                                        <Tag color={healthTagColor} style={{ fontWeight: 700, borderRadius: 12 }}>
                                            {health.indicator_label || 'Healthy'}
                                        </Tag>
                                    </div>
                                    <Row align="middle" gutter={16}>
                                        <Col span={10}>
                                            <Progress
                                                type="circle"
                                                percent={health.score ?? 80}
                                                strokeColor={healthColor}
                                                size={75}
                                                format={(percent) => <span style={{ fontWeight: 800, fontSize: 18 }}>{percent}</span>}
                                            />
                                        </Col>
                                        <Col span={14}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Factors evaluated:
                                            </Text>
                                            <div style={{ fontSize: 11, marginTop: 4 }}>
                                                <div>• Activity &amp; Logins: <strong>{health.score_breakdown?.login_frequency?.points || 0}/20</strong></div>
                                                <div>• Module Usage: <strong>{health.score_breakdown?.module_usage?.points || 0}/20</strong></div>
                                                <div>• Critical Actions: <strong>{health.score_breakdown?.open_critical_actions?.points || 0}/15</strong></div>
                                                <div>• Support Status: <strong>{health.score_breakdown?.support_complaints?.points || 0}/15</strong></div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Card 2: Package & Renewal */}
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
                                height: '100%'
                            }}
                        >
                            {loading ? (
                                <Skeleton active paragraph={{ rows: 3 }} />
                            ) : (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <Text type="secondary" strong style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                                            Package &amp; Renewal
                                        </Text>
                                        <Tag color="blue">{pkg.package_name || 'ENTERPRISE'}</Tag>
                                    </div>
                                    <Statistic
                                        title="Contract Expiry"
                                        value={pkg.end_date || 'No Expiry Set'}
                                        prefix={<CalendarOutlined style={{ color: '#3b82f6' }} />}
                                        valueStyle={{ fontSize: 18, fontWeight: 700 }}
                                    />
                                    <Divider style={{ margin: '12px 0' }} />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                        <span>Billing: <strong>{pkg.billing_status || 'Active (Paid)'}</strong></span>
                                        <span>Auto-Renewal: <strong style={{ color: pkg.auto_renewal ? '#10b981' : '#f59e0b' }}>{pkg.auto_renewal ? 'Yes' : 'Manual'}</strong></span>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Card 3: Provisioned Users & Activity */}
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
                                height: '100%'
                            }}
                        >
                            {loading ? (
                                <Skeleton active paragraph={{ rows: 3 }} />
                            ) : (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <Text type="secondary" strong style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                                            Users &amp; Platform Access
                                        </Text>
                                        <Badge status="processing" text={`${telemetry.active_users || 1} Active`} />
                                    </div>
                                    <Statistic
                                        title="Total Provisioned Users"
                                        value={telemetry.total_users || 1}
                                        prefix={<TeamOutlined style={{ color: '#06b6d4' }} />}
                                        valueStyle={{ fontSize: 22, fontWeight: 800 }}
                                    />
                                    <Divider style={{ margin: '12px 0' }} />
                                    <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <ClockCircleOutlined style={{ color: '#64748b' }} />
                                        <Text type="secondary">Last Login:</Text>
                                        <Text strong>{telemetry.last_login_date || 'Active Today'}</Text>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Card 4: Storage & Platform Adoption */}
                    <Col xs={24} sm={12} lg={6}>
                        <Card
                            bordered={false}
                            style={{
                                borderRadius: 16,
                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
                                height: '100%'
                            }}
                        >
                            {loading ? (
                                <Skeleton active paragraph={{ rows: 3 }} />
                            ) : (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <Text type="secondary" strong style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                                            Storage &amp; Adoption
                                        </Text>
                                        <Tag color="cyan">{telemetry.platform_adoption_score || 0}% Adoption</Tag>
                                    </div>
                                    <Statistic
                                        title="Storage Utilization"
                                        value={`${telemetry.storage_usage?.used_mb || 0} MB`}
                                        prefix={<CloudServerOutlined style={{ color: '#8b5cf6' }} />}
                                        suffix={`/ ${Math.round((telemetry.storage_usage?.limit_mb || 10240) / 1024)} GB`}
                                        valueStyle={{ fontSize: 18, fontWeight: 700 }}
                                    />
                                    <Progress
                                        percent={telemetry.storage_usage?.usage_pct || 1}
                                        size="small"
                                        strokeColor="#8b5cf6"
                                        style={{ margin: '8px 0 4px 0' }}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b' }}>
                                        <span>Tickets: <strong>{risk.open_support_tickets || 0} Open</strong></span>
                                        <span>Onboarding: <strong>{risk.onboarding_progress_pct || 100}% Done</strong></span>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>

                {/* 3. Detailed Bento Tabs */}
                <Card
                    bordered={false}
                    style={{
                        borderRadius: 16,
                        boxShadow: '0 2px 14px rgba(0, 0, 0, 0.04)'
                    }}
                >
                    {loading ? (
                        <div style={{ padding: '24px 0' }}>
                            <Skeleton active paragraph={{ rows: 10 }} />
                        </div>
                    ) : (
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            size="large"
                            items={[
                                {
                                    key: 'overview',
                                    label: (
                                        <span>
                                            <BankOutlined /> Overview &amp; Governance
                                        </span>
                                    ),
                                    children: (
                                        <Row gutter={[24, 24]} style={{ marginTop: 12 }}>
                                            {/* Primary Contact Details */}
                                            <Col xs={24} lg={12}>
                                                <Card type="inner" title={<span><UserOutlined style={{ color: '#3b82f6' }} /> Primary Administrator Details</span>}>
                                                    <Row gutter={[16, 16]}>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Contact Person"
                                                                value={`${cp.first_name || ''} ${cp.last_name || ''}`.trim() || '—'}
                                                                icon={<UserOutlined style={{ color: '#3b82f6' }} />}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Corporate Email"
                                                                value={cp.email}
                                                                icon={<MailOutlined style={{ color: '#3b82f6' }} />}
                                                                copyable
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Phone Number"
                                                                value={cp.phone_number}
                                                                icon={<PhoneOutlined style={{ color: '#10b981' }} />}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Company Website"
                                                                value={cp.company_website}
                                                                icon={<GlobalOutlined style={{ color: '#06b6d4' }} />}
                                                                isLink={Boolean(cp.company_website)}
                                                                linkHref={cp.company_website ? (cp.company_website.startsWith('http') ? cp.company_website : `https://${cp.company_website}`) : null}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Employees / Headcount"
                                                                value={cp.employees}
                                                                icon={<TeamOutlined style={{ color: '#8b5cf6' }} />}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Estimated Revenue"
                                                                value={cp.revenue}
                                                                icon={<DollarOutlined style={{ color: '#f59e0b' }} />}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            </Col>

                                            {/* Governance & Internal Assignments */}
                                            <Col xs={24} lg={12}>
                                                <Card type="inner" title={<span><SafetyCertificateOutlined style={{ color: '#10b981' }} /> Governance &amp; Assignments</span>}>
                                                    <Row gutter={[16, 16]}>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Assigned VRM"
                                                                value={gov.vrm?.name || 'Unassigned'}
                                                                icon={<SafetyCertificateOutlined style={{ color: '#10b981' }} />}
                                                                extraTag={gov.vrm?.allocated_hours_per_week > 0 ? (
                                                                    <Tag color="cyan" style={{ marginLeft: 6 }}>{gov.vrm.allocated_hours_per_week}h/week</Tag>
                                                                ) : null}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Implementation Consultant"
                                                                value={gov.implementation_consultant?.name || 'CARA Deployment Team'}
                                                                icon={<CustomerServiceOutlined style={{ color: '#3b82f6' }} />}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Default Timezone"
                                                                value={scope.timezone || 'UTC'}
                                                                icon={<ClockCircleOutlined style={{ color: '#64748b' }} />}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Primary Language"
                                                                value={(scope.default_language || 'EN').toUpperCase()}
                                                                icon={<GlobalOutlined style={{ color: '#8b5cf6' }} />}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Provisioned By"
                                                                value={cp.created_by_superadmin || 'System Admin'}
                                                                icon={<CrownOutlined style={{ color: '#f59e0b' }} />}
                                                            />
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <DetailField
                                                                label="Account Created"
                                                                value={cp.created_at ? String(cp.created_at).slice(0, 10) : '—'}
                                                                icon={<CalendarOutlined style={{ color: '#06b6d4' }} />}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            </Col>
                                        </Row>
                                    )
                                },
                                {
                                    key: 'modules',
                                    label: (
                                        <span>
                                            <AppstoreOutlined /> Frameworks &amp; Modules
                                        </span>
                                    ),
                                    children: (
                                        <>
                                        <Row gutter={[24, 24]} style={{ marginTop: 12 }}>
                                            <Col xs={24} lg={12}>
                                                <Card type="inner" title={<span><SafetyCertificateOutlined style={{ color: '#3b82f6' }} /> Assigned Standards &amp; Frameworks ({scope.assigned_frameworks?.length || 0})</span>}>
                                                    <div style={{ minHeight: 120 }}>
                                                        {scope.assigned_frameworks && scope.assigned_frameworks.length > 0 ? (
                                                            <Space wrap size={[8, 12]}>
                                                                {scope.assigned_frameworks.map((f, i) => (
                                                                    <Tag color="blue" key={i} style={{ padding: '6px 14px', fontSize: 13, borderRadius: 8 }}>
                                                                        <CheckCircleOutlined style={{ marginRight: 6 }} /> {f}
                                                                    </Tag>
                                                                ))}
                                                            </Space>
                                                        ) : (
                                                            <Text type="secondary">No specific compliance frameworks assigned.</Text>
                                                        )}
                                                    </div>
                                                </Card>
                                            </Col>

                                            <Col xs={24} lg={12}>
                                                <Card type="inner" title={<span><ApartmentOutlined style={{ color: '#8b5cf6' }} /> Assigned Platform Modules ({scope.assigned_modules?.length || 0})</span>}>
                                                    <div style={{ minHeight: 120 }}>
                                                        {scope.assigned_modules && scope.assigned_modules.length > 0 ? (
                                                            <Space wrap size={[8, 12]}>
                                                                {scope.assigned_modules.map((m, i) => (
                                                                    <Tag color="purple" key={i} style={{ padding: '6px 14px', fontSize: 13, borderRadius: 8 }}>
                                                                        <ThunderboltOutlined style={{ marginRight: 6 }} /> {m}
                                                                    </Tag>
                                                                ))}
                                                            </Space>
                                                        ) : (
                                                            <Text type="secondary">No specific platform modules assigned.</Text>
                                                        )}
                                                    </div>
                                                </Card>
                                            </Col>
                                        </Row>

                                        {/* 20-Day Module Usage Telemetry Section */}
                                        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                                            <Col span={24}>
                                                <Card 
                                                    type="inner" 
                                                    title={
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 8 }}>
                                                            <span>
                                                                <FireOutlined style={{ color: '#ef4444', marginRight: 8 }} />
                                                                Module Activity &amp; Usage Telemetry (Last 20 Days)
                                                            </span>
                                                            <Tag color="cyan" style={{ fontSize: 12 }}>Filtered: Last 20 Days Audit Logs</Tag>
                                                        </div>
                                                    }
                                                >
                                                    <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                                        <Col xs={12} sm={6}>
                                                            <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                <Statistic
                                                                    title="Total Activities (20d)"
                                                                    value={moduleUsage20d.total_activities || 0}
                                                                    valueStyle={{ color: '#3b82f6', fontWeight: 800 }}
                                                                    prefix={<DashboardOutlined />}
                                                                />
                                                            </Card>
                                                        </Col>
                                                        <Col xs={12} sm={6}>
                                                            <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                <Statistic
                                                                    title="Most Visited / Active"
                                                                    value={moduleUsage20d.most_active_module || 'None'}
                                                                    valueStyle={{ color: '#10b981', fontWeight: 800, fontSize: 14 }}
                                                                    prefix={<FireOutlined />}
                                                                />
                                                            </Card>
                                                        </Col>
                                                        <Col xs={12} sm={6}>
                                                            <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                <Statistic
                                                                    title="Active Modules"
                                                                    value={moduleUsage20d.active_modules_count || 0}
                                                                    valueStyle={{ color: '#8b5cf6', fontWeight: 800 }}
                                                                    suffix={`/ ${scope.assigned_modules?.length || 0}`}
                                                                />
                                                            </Card>
                                                        </Col>
                                                        <Col xs={12} sm={6}>
                                                            <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                <Statistic
                                                                    title="Dormant Modules"
                                                                    value={moduleUsage20d.dormant_modules_count || 0}
                                                                    valueStyle={{ color: '#64748b', fontWeight: 800 }}
                                                                    suffix="modules"
                                                                />
                                                            </Card>
                                                        </Col>
                                                    </Row>

                                                    <Table
                                                        dataSource={moduleUsage20d.modules_breakdown || []}
                                                        columns={moduleUsageColumns}
                                                        rowKey="module"
                                                        pagination={false}
                                                        size="middle"
                                                        locale={{ emptyText: 'No audit activities recorded for assigned modules in the last 20 days.' }}
                                                    />
                                                </Card>
                                            </Col>
                                        </Row>
                                        </>
                                    )
                                },
                                                                {
                                    key: 'users_directory',
                                    label: (
                                        <span>
                                            <TeamOutlined /> Users &amp; TPRM Directory ({userCounts.total || (internalUsers.length + tprmAdmins.length + tprmUsers.length + 1)})
                                        </span>
                                    ),
                                    children: (
                                        <div style={{ marginTop: 12 }}>
                                            <Tabs
                                                type="card"
                                                defaultActiveKey="internal_users"
                                                items={[
                                                    {
                                                        key: 'internal_users',
                                                        label: (
                                                            <span>
                                                                <ApartmentOutlined /> Internal CARA Team ({internalUsers.length})
                                                            </span>
                                                        ),
                                                        children: (
                                                            <div style={{ marginTop: 8 }}>
                                                                {/* Top Metrics Banner */}
                                                                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Internal Team Members"
                                                                                value={internalUsers.length}
                                                                                valueStyle={{ color: '#3b82f6', fontWeight: 800 }}
                                                                                prefix={<TeamOutlined />}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Active Accounts"
                                                                                value={internalUsers.filter(u => u.status === 'Active').length}
                                                                                valueStyle={{ color: '#10b981', fontWeight: 800 }}
                                                                                prefix={<CheckCircleOutlined />}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Password Activated"
                                                                                value={internalUsers.filter(u => u.is_password_set).length}
                                                                                valueStyle={{ color: '#8b5cf6', fontWeight: 800 }}
                                                                                suffix={`/ ${internalUsers.length}`}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Agreements Accepted"
                                                                                value={internalUsers.filter(u => u.agreement_accepted).length}
                                                                                valueStyle={{ color: '#06b6d4', fontWeight: 800 }}
                                                                                suffix={`/ ${internalUsers.length}`}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                </Row>

                                                                {/* Primary Tenant Administrator Card */}
                                                                {primaryAdmin.name && (
                                                                    <Card 
                                                                        type="inner" 
                                                                        style={{ marginBottom: 20, borderColor: '#bfdbfe', background: '#eff6ff' }}
                                                                        title={
                                                                            <Space>
                                                                                <CrownOutlined style={{ color: '#eab308' }} />
                                                                                <Text strong>Primary CARA Administrator (Tenant Owner)</Text>
                                                                                <Tag color="gold">Root Admin</Tag>
                                                                            </Space>
                                                                        }
                                                                    >
                                                                        <Row gutter={[16, 16]} align="middle">
                                                                            <Col xs={24} md={6}>
                                                                                <Space>
                                                                                    <Avatar size={48} style={{ backgroundColor: '#2563eb', fontWeight: 700 }}>
                                                                                        {primaryAdmin.name[0]}
                                                                                    </Avatar>
                                                                                    <div>
                                                                                        <Text strong style={{ fontSize: 14, display: 'block' }}>{primaryAdmin.name}</Text>
                                                                                        <Text code style={{ fontSize: 11 }}>{primaryAdmin.uid || cp.uid || '—'}</Text>
                                                                                    </div>
                                                                                </Space>
                                                                            </Col>
                                                                            <Col xs={24} sm={12} md={6}>
                                                                                <MailOutlined style={{ marginRight: 6, color: '#3b82f6' }} />
                                                                                <Text copyable style={{ fontSize: 12 }}>{primaryAdmin.email}</Text>
                                                                                <div style={{ marginTop: 2 }}>
                                                                                    <PhoneOutlined style={{ marginRight: 6, color: '#10b981' }} />
                                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{primaryAdmin.phone || '—'}</Text>
                                                                                </div>
                                                                            </Col>
                                                                            <Col xs={24} sm={12} md={6}>
                                                                                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Role &amp; Privilege</Text>
                                                                                <Tag color="purple">{primaryAdmin.role_name || 'CARA Admin'}</Tag>
                                                                                <Badge status="success" text={<Text style={{ fontSize: 12 }}>{primaryAdmin.status}</Text>} />
                                                                            </Col>
                                                                            <Col xs={24} md={6}>
                                                                                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Last Admin Session</Text>
                                                                                <Text style={{ fontSize: 12, fontWeight: 500 }}>
                                                                                    <ClockCircleOutlined style={{ marginRight: 4, color: '#3b82f6' }} />
                                                                                    {primaryAdmin.last_login}
                                                                                </Text>
                                                                            </Col>
                                                                        </Row>
                                                                    </Card>
                                                                )}

                                                                {/* Table of Users Created by this CARA Admin */}
                                                                <Card 
                                                                    type="inner" 
                                                                    title={
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 8 }}>
                                                                            <span>
                                                                                <TeamOutlined style={{ color: '#3b82f6', marginRight: 8 }} />
                                                                                Internal Team Members ({internalUsers.length})
                                                                            </span>
                                                                            <Tag color="blue">Created by {primaryAdmin.name || cp.company_name || 'CARA Admin'}</Tag>
                                                                        </div>
                                                                    }
                                                                >
                                                                    <Table
                                                                        dataSource={internalUsers}
                                                                        columns={provisionedUserColumns}
                                                                        rowKey="user_id"
                                                                        pagination={internalUsers.length > 10 ? { pageSize: 10 } : false}
                                                                        size="middle"
                                                                        locale={{ emptyText: 'No internal team members created yet.' }}
                                                                    />
                                                                </Card>
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        key: 'tprm_admins',
                                                        label: (
                                                            <span>
                                                                <SafetyCertificateOutlined /> TPRM Admins / Vendor Leads ({tprmAdmins.length})
                                                            </span>
                                                        ),
                                                        children: (
                                                            <div style={{ marginTop: 8 }}>
                                                                {/* TPRM Admins KPI Banner */}
                                                                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Total Vendor Leads"
                                                                                value={tprmAdmins.length}
                                                                                valueStyle={{ color: '#f97316', fontWeight: 800 }}
                                                                                prefix={<SafetyCertificateOutlined />}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Under Review"
                                                                                value={tprmAdmins.filter(a => a.status === 'Under Review').length}
                                                                                valueStyle={{ color: '#3b82f6', fontWeight: 800 }}
                                                                                prefix={<ClockCircleOutlined />}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Active Vendors"
                                                                                value={tprmAdmins.filter(a => a.status === 'Active').length}
                                                                                valueStyle={{ color: '#10b981', fontWeight: 800 }}
                                                                                prefix={<CheckCircleOutlined />}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Low Risk Rated"
                                                                                value={tprmAdmins.filter(a => a.risk_rating === 'Low').length}
                                                                                valueStyle={{ color: '#10b981', fontWeight: 800 }}
                                                                                suffix={`/ ${tprmAdmins.length}`}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                </Row>

                                                                <Card 
                                                                    type="inner" 
                                                                    title={
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 8 }}>
                                                                            <span>
                                                                                <SafetyCertificateOutlined style={{ color: '#f97316', marginRight: 8 }} />
                                                                                Third-Party TPRM Administrators ({tprmAdmins.length})
                                                                            </span>
                                                                            <Tag color="orange">Primary Vendor Contacts</Tag>
                                                                        </div>
                                                                    }
                                                                >
                                                                    <Table
                                                                        dataSource={tprmAdmins}
                                                                        columns={tprmAdminColumns}
                                                                        rowKey="id"
                                                                        pagination={tprmAdmins.length > 10 ? { pageSize: 10 } : false}
                                                                        size="middle"
                                                                        locale={{ emptyText: 'No TPRM vendor administrators registered yet.' }}
                                                                    />
                                                                </Card>
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        key: 'tprm_users',
                                                        label: (
                                                            <span>
                                                                <TeamOutlined /> TPRM Users / Vendor Staff ({tprmUsers.length})
                                                            </span>
                                                        ),
                                                        children: (
                                                            <div style={{ marginTop: 8 }}>
                                                                {/* TPRM Users KPI Banner */}
                                                                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Total Vendor Users"
                                                                                value={tprmUsers.length}
                                                                                valueStyle={{ color: '#8b5cf6', fontWeight: 800 }}
                                                                                prefix={<TeamOutlined />}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Active Users"
                                                                                value={tprmUsers.filter(u => u.status === 'Active').length}
                                                                                valueStyle={{ color: '#10b981', fontWeight: 800 }}
                                                                                prefix={<CheckCircleOutlined />}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Passwords Set"
                                                                                value={tprmUsers.filter(u => u.is_password_set).length}
                                                                                valueStyle={{ color: '#3b82f6', fontWeight: 800 }}
                                                                                suffix={`/ ${tprmUsers.length}`}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                    <Col xs={12} sm={6}>
                                                                        <Card size="small" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                                            <Statistic
                                                                                title="Agreements Accepted"
                                                                                value={tprmUsers.filter(u => u.agreement_accepted).length}
                                                                                valueStyle={{ color: '#06b6d4', fontWeight: 800 }}
                                                                                suffix={`/ ${tprmUsers.length}`}
                                                                            />
                                                                        </Card>
                                                                    </Col>
                                                                </Row>

                                                                <Card 
                                                                    type="inner" 
                                                                    title={
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 8 }}>
                                                                            <span>
                                                                                <TeamOutlined style={{ color: '#8b5cf6', marginRight: 8 }} />
                                                                                Vendor Staff &amp; Secondary Users ({tprmUsers.length})
                                                                            </span>
                                                                            <Tag color="purple">Sub-users provisioned under Vendor TPINs</Tag>
                                                                        </div>
                                                                    }
                                                                >
                                                                    <Table
                                                                        dataSource={tprmUsers}
                                                                        columns={tprmUserColumns}
                                                                        rowKey="id"
                                                                        pagination={tprmUsers.length > 10 ? { pageSize: 10 } : false}
                                                                        size="middle"
                                                                        locale={{ emptyText: 'No TPRM sub-users or vendor staff registered yet.' }}
                                                                    />
                                                                </Card>
                                                            </div>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </div>
                                    )
                                },
{
                                    key: 'onboarding_tickets',
                                    label: (
                                        <span>
                                            <CheckSquareOutlined /> Onboarding &amp; Support Tickets
                                            {risk.open_support_tickets > 0 && (
                                                <Badge count={risk.open_support_tickets} style={{ marginLeft: 8 }} />
                                            )}
                                        </span>
                                    ),
                                    children: (
                                        <Row gutter={[24, 24]} style={{ marginTop: 12 }}>
                                            <Col xs={24} lg={12}>
                                                <Card type="inner" title={<span><CheckCircleOutlined style={{ color: '#10b981' }} /> Onboarding Checkpoints ({risk.onboarding_progress_pct || 100}%)</span>}>
                                                    <Progress percent={risk.onboarding_progress_pct || 100} status="active" style={{ marginBottom: 16 }} />
                                                    <List
                                                        size="small"
                                                        dataSource={risk.onboarding_tasks || []}
                                                        renderItem={(item) => (
                                                            <List.Item
                                                                actions={[
                                                                    item.completed ? (
                                                                        <Tag color="success">Completed</Tag>
                                                                    ) : (
                                                                        <Tag color="warning">Pending</Tag>
                                                                    )
                                                                ]}
                                                            >
                                                                <Space>
                                                                    {item.completed ? (
                                                                        <CheckCircleOutlined style={{ color: '#10b981' }} />
                                                                    ) : (
                                                                        <ClockCircleOutlined style={{ color: '#f59e0b' }} />
                                                                    )}
                                                                    <Text delete={item.completed}>{item.title}</Text>
                                                                </Space>
                                                            </List.Item>
                                                        )}
                                                    />
                                                </Card>
                                            </Col>

                                            <Col xs={24} lg={12}>
                                                <Card type="inner" title={<span><CustomerServiceOutlined style={{ color: '#3b82f6' }} /> Support Inquiries ({risk.open_support_tickets || 0} Open)</span>}>
                                                    <Table
                                                        dataSource={risk.tickets_list || []}
                                                        columns={ticketColumns}
                                                        rowKey="request_id"
                                                        pagination={false}
                                                        size="small"
                                                        locale={{ emptyText: 'No open support tickets or escalations' }}
                                                    />
                                                </Card>
                                            </Col>
                                        </Row>
                                    )
                                },
                                
                                {
                                    key: 'audit_log',
                                    label: (
                                        <span>
                                            <HistoryOutlined /> Audit Activity
                                        </span>
                                    ),
                                    children: (
                                        <div style={{ marginTop: 12 }}>
                                            <Table
                                                dataSource={audit.recent_administrative_actions || []}
                                                columns={auditColumns}
                                                rowKey={(rec, index) => `${rec.timestamp}-${index}`}
                                                size="middle"
                                                pagination={{ pageSize: 5 }}
                                                locale={{ emptyText: 'No recent administrative activity logged' }}
                                            />
                                        </div>
                                    )
                                }
                            ]}
                        />
                    )}
                </Card>

            </div>
        </div>
    );
};

export default Client360View;
